import { useState, useEffect } from 'react';
import { Drawer, drawerType } from '../../../packages/ui/organisms/drawer';
import {
  Avatar,
  avatarType,
  Badge,
  badgeType,
  badgeStyle,
  colors,
  fontWeight,
  IconButton,
  Icons,
  Text,
  textLevel,
} from '../../../packages';
import { Box, Button, Menu, MenuItem, Divider } from '@mui/material';
import avatarIcon from '../../../assets/images/avatar.png';
import groupAvatarIcon from '../../../assets/images/group-avatar.png';
import { sxStyles } from './ChatDrawer.styles';
import { MessageInputField } from '../../../packages/ui/templates/message-input-field';
import { sendMessage, refreshMessage } from '../../../services/report-service/reportService.service';
import { useSelector } from 'react-redux';
import { getAuth } from '../../../redux/modules/auth/selectors';
import { showSnackbar } from '../../../redux/modules/snackbar';
import { useDispatch } from 'react-redux';
import { sentenceCase } from 'sentence-case';
import dayjs from 'dayjs';
import { getProfile } from '../../../redux/modules/profile/selectors';

const ChatDrawer = ({
  isOpen,
  onClose,
  selectedContact,
  getProfilePictureFromChannel,
  getConnectionDetailsByUserId,
  getSelectedContactFullName,
  connectionTypes,
  memberId,
  params,
  onSync,
  isLoadingMessages,
  messagesData,
  setMessagesData,
  nextCursor,
  hasMoreMessages,
  fetchMessages,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const { meta } = useSelector(getAuth);

  const { demographicDetails } = useSelector(getProfile);

  const profilePicture =
    getProfilePictureFromChannel(params.get('userId')) ||
    getConnectionDetailsByUserId(selectedContact?.id)?.profilePicture;

  const engagement = {
    Status: demographicDetails?.levelOfEngagementStatus,
    SubStatus: demographicDetails?.levelOfEngagementSubStatus,
  };

  const getBadgeStyle = () => {
    if (engagement?.SubStatus === 'Engaged') {
      return badgeStyle.RESOLVED;
    } else if (engagement?.SubStatus === 'Outreach needed') {
      return badgeStyle.MEDIUM;
    } else {
      return badgeStyle.CRITICAL;
    }
  };

  const handleSync = async () => {
    try {
      // Create query params with memberId
      const queryParams = {
        memberId: memberId || selectedContact?.connectionId,
      };

      // Call the refreshMessage API
      await refreshMessage(queryParams);

      // Show success message
      dispatch(
        showSnackbar({
          snackType: 'success',
          snackMessage: 'Messages synced successfully',
        }),
      );

      // Refresh messages to get the latest data
      fetchMessages();

      // Call the onSync prop if it exists (for backward compatibility)
      if (onSync && selectedContact?.id) {
        onSync(selectedContact?.id);
      }
    } catch (error) {
      console.error('Error syncing messages:', error);
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to sync messages. Please try again.',
        }),
      );
    }
  };

  const loadMoreMessages = () => {
    if (hasMoreMessages && nextCursor && !isLoadingMessages) {
      fetchMessages(nextCursor);
    }
  };

  const handleInputChange = e => {
    setInputValue(e.target.value);
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() && !isSending) {
      setIsSending(true);

      const newMessage = {
        id: Date.now(),
        text: inputValue,
        timestamp: dayjs().format('h:mm A'),
        createdAt: new Date(),
      };

      try {
        // Prepare message data for API - only include the required fields
        const messageData = {
          memberId: memberId || selectedContact?.connectionId,
          userId: meta?.userId,
          content: inputValue.trim(),
        };

        // Call the API to send the message
        await sendMessage(messageData);

        // Update UI with the new message
        setMessagesData([...messagesData, newMessage]);
        setInputValue('');

        // Refresh messages to ensure we have the latest data
        setTimeout(() => fetchMessages(), 500);
      } catch (error) {
        console.error('Error sending message:', error);

        // Handle specific error cases
        if (error.response?.data?.message) {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: `Failed to send message: ${error.response.data.message}`,
            }),
          );
        } else {
          dispatch(
            showSnackbar({
              snackType: 'error',
              snackMessage: 'Failed to send message. Please try again.',
            }),
          );
        }
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {isOpen && (
        <Drawer
          open={isOpen}
          onClose={() => onClose(false)}
          title={`Text messages with ${getSelectedContactFullName()}`}
          variant={drawerType.FORM}
          sx={sxStyles.drawer}
          widthVariant="NARROW"
          footer={<></>}
        >
          <Box sx={sxStyles.demographics}>
            <Box sx={sxStyles['demographics.left']}>
              <Box sx={{ flexShrink: 0 }}>
                {profilePicture ? (
                  <Avatar
                    variant={avatarType.CIRCLE}
                    size={40}
                    src={profilePicture}
                    name={getSelectedContactFullName()}
                  />
                ) : (
                  <Box maxWidth={40} maxHeight={40} width={40} height={40}>
                    <img
                      src={
                        selectedContact?.type !== connectionTypes.CHAT_GROUP ? avatarIcon : groupAvatarIcon
                      }
                      alt="avatar"
                      width="40px"
                      height="40px"
                    />
                  </Box>
                )}
              </Box>
              <Box sx={sxStyles.wraper}>
                <Box sx={sxStyles['demographics.nameBadge']}>
                  <Box sx={sxStyles['demographics.name']}>{getSelectedContactFullName()}</Box>
                  <Box>
                    {engagement?.Status !== '' ? (
                      <Badge variant={badgeType.FILLED} style={getBadgeStyle()}>
                        {sentenceCase(engagement?.Status || '')}
                      </Badge>
                    ) : (
                      <Box>{engagement?.Status}</Box>
                    )}
                  </Box>
                </Box>
                <Box sx={sxStyles['demographics.id']}>{`${selectedContact?.uuid}`}</Box>
              </Box>
            </Box>

            <Box sx={sxStyles['demographics.right']}>
              <Box sx={sxStyles.info}>
                <Box sx={sxStyles['demographics.email']}>{selectedContact?.email}</Box>
                <Box sx={sxStyles['demographics.phone']}>{selectedContact?.phone || ''}</Box>
              </Box>
              <Box sx={sxStyles.actionItem}>
                <Button variant="outlined" onClick={handleClick} sx={sxStyles.menuButtonHeader}>
                  <Icons glyph="more" color={colors.primary500} />
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      handleSync();
                      handleClose();
                    }}
                    sx={sxStyles.menuItem}
                  >
                    Sync
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Box>

          <Divider sx={sxStyles.divider} />

          <Box sx={sxStyles.messagesContainer}>
            {isLoadingMessages && messagesData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Text level={textLevel.M} color={colors.neutral900}>
                  Loading messages...
                </Text>
              </Box>
            ) : messagesData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Text level={textLevel.M} color={colors.neutral900}>
                  No messages found. Start a conversation!
                </Text>
              </Box>
            ) : (
              <>
                {hasMoreMessages && (
                  <Box display="flex" justifyContent="center" my={2}>
                    <Text
                      level={textLevel.S}
                      color={colors.primary500}
                      sx={{ cursor: 'pointer' }}
                      onClick={loadMoreMessages}
                    >
                      {isLoadingMessages ? 'Loading more messages...' : 'Load more messages'}
                    </Text>
                  </Box>
                )}
                {messagesData.map(message => (
                  <Box key={message.id} sx={sxStyles.messageGroup}>
                    <Box
                      sx={{
                        ...sxStyles.messageItem,
                        ...(message.sender === 'sender' ? sxStyles.sentMessage : sxStyles.receivedMessage),
                      }}
                    >
                      <Box sx={sxStyles.messageContent}>
                        <Text
                          level={textLevel.M}
                          weight={fontWeight.MEDIUM}
                          color={message.sender === 'sender' ? colors.white : colors.neutral900}
                          sx={sxStyles.messageText}
                        >
                          {message.text}
                        </Text>
                        <Text
                          level={textLevel.XS}
                          color={message.sender === 'sender' ? colors.white : colors.neutral600}
                          sx={{
                            ...(message.sender === 'sender'
                              ? sxStyles.sentTimestamp
                              : sxStyles.receivedTimestamp),
                            opacity: message.sender === 'sender' ? 0.7 : 1,
                          }}
                        >
                          {message.timestamp}
                        </Text>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </Box>

          <Divider sx={sxStyles.divider} />

          <Box sx={sxStyles.footer}>
            <Box sx={sxStyles.inputWrapper}>
              <MessageInputField
                sx={sxStyles.inputField}
                placeholder={`Send text message to ${getSelectedContactFullName()}`}
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                disabled={isSending}
              />
              <IconButton
                sx={sxStyles.sendButton}
                onClick={handleSendMessage}
                disabled={isSending || !inputValue.trim()}
              >
                <Icons glyph="send-filled" />
              </IconButton>
            </Box>
          </Box>
        </Drawer>
      )}
    </>
  );
};

export default ChatDrawer;
